import React, { useState, useEffect } from 'react';
import { Gift, Sparkles, Calendar, Star } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const CouponPromoSection = () => {
  const [isEnabled, setIsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/coupon-promo`);
      if (response.ok) {
        const data = await response.json();
        setIsEnabled(data.enabled !== false); // Default true
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !isEnabled) {
    return null;
  }

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#F5E6D3] via-[#FAF0E6] to-[#FFF8DC]">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Main Message */}
          <div>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C8E6C9]/50 to-[#A5D6A7]/40 px-4 py-2 rounded-full mb-6 border border-[#81C784]/40">
              <Sparkles className="h-5 w-5 text-[#2E7D32]" />
              <span className="font-semibold text-[#2E7D32]">Exklusiv für Neukunden</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-[#8B7355] to-[#A0826D] bg-clip-text text-transparent">
              Willkommensgeschenk: 4 Coupons geschenkt! 🎁
            </h2>
            
            <p className="text-lg text-[#6B5744] mb-8 leading-relaxed">
              Melden Sie sich jetzt an und erhalten Sie <strong className="text-[#8B7355]">4 Willkommens-Coupons</strong> mit jeweils <strong className="text-[#8B7355]">10% Rabatt</strong> auf Ihre Einkäufe. 
              Zusätzlich erwarten Sie zu besonderen Feiertagen wie Weihnachten, Ostern und Halloween <strong className="text-[#8B7355]">exklusive Bonus-Coupons mit bis zu 20% Rabatt</strong>!
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-gradient-to-r from-[#8B7355] to-[#A0826D] hover:from-[#7A6449] hover:to-[#8B7355] text-white font-bold shadow-lg border-2 border-[#8B7355]"
                onClick={() => navigate('/register')}
              >
                <Gift className="h-5 w-5 mr-2" />
                Jetzt registrieren & Coupons erhalten
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-[#8B7355] text-[#8B7355] hover:bg-[#F5E6D3]"
                onClick={() => navigate('/my-coupons')}
              >
                Meine Coupons ansehen
              </Button>
            </div>
          </div>

          {/* Right: Benefits Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Benefit 1 - Pastell Rosa */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border-2 border-[#FFB6C1]/40 shadow-md hover:shadow-lg transition-all hover:border-[#FFB6C1]/60 hover:bg-[#FFF0F5]/30">
              <div className="bg-gradient-to-br from-[#FFE4E1] to-[#FFF0F5] rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Gift className="h-6 w-6 text-[#FF69B4]" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-[#8B7355]">4x Willkommens-Coupons</h3>
              <p className="text-[#6B5744] text-sm">
                Sofort nach Registrierung erhalten Sie 4 Coupons à 10% Rabatt
              </p>
            </div>

            {/* Benefit 2 - Pastell Grün */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border-2 border-[#98D8C8]/40 shadow-md hover:shadow-lg transition-all hover:border-[#98D8C8]/60 hover:bg-[#F0FFF4]/30">
              <div className="bg-gradient-to-br from-[#E0F2F1] to-[#F0FFF4] rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Calendar className="h-6 w-6 text-[#5F9EA0]" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-[#8B7355]">Feiertags-Specials</h3>
              <p className="text-[#6B5744] text-sm">
                Zu Weihnachten, Ostern & Co. gibt's Extra-Coupons mit bis zu 20%
              </p>
            </div>

            {/* Benefit 3 - Pastell Blau */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border-2 border-[#B0E0E6]/40 shadow-md hover:shadow-lg transition-all hover:border-[#B0E0E6]/60 hover:bg-[#F0F8FF]/30">
              <div className="bg-gradient-to-br from-[#E6F3FF] to-[#F0F8FF] rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Star className="h-6 w-6 text-[#4682B4]" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-[#8B7355]">Treue-Bonus</h3>
              <p className="text-[#6B5744] text-sm">
                Nach 5 Bestellungen erhalten Sie einen speziellen 20% Treue-Coupon
              </p>
            </div>

            {/* Benefit 4 - Pastell Gelb */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border-2 border-[#F0E68C]/40 shadow-md hover:shadow-lg transition-all hover:border-[#F0E68C]/60 hover:bg-[#FFFACD]/30">
              <div className="bg-gradient-to-br from-[#FFF9E6] to-[#FFFACD] rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Sparkles className="h-6 w-6 text-[#DAA520]" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-[#8B7355]">90 Tage gültig</h3>
              <p className="text-[#6B5744] text-sm">
                Alle Coupons sind 90 Tage ab Erhalt gültig – genug Zeit zum Shoppen!
              </p>
            </div>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-12 text-center">
          <p className="text-[#6B5744] text-sm">
            * Pro Bestellung kann nur 1 Coupon eingelöst werden. Mindestbestellwert: 30€. Nicht kombinierbar mit Sale-Artikeln.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CouponPromoSection;
